import React from 'react';
import { useResetOnKeyChange } from '@/utils';
import {
    useBarcodeIssuesQuery,
    useFixBarcodeIssuesMutation,
} from '@/api/queries';
import { BARCODE_FIX_MAX_ITEMS } from '@/api/handlers/resources/products/requests';
import type { BarcodeFixAction, BarcodeIssue } from '@/api/models';
import {
    PRODUCT_DEFAULT_PAGE_SIZE,
    getPaginationWindow,
} from '../productsMeta';
import {
    barcodeIssueKeyOf,
    buildFixItems,
    choiceOf,
    isChoiceReady,
    readRefusal,
    variantCountOf,
    type BarcodeIssueChoice,
    type BarcodeIssuesRefusal,
    type BarcodeIssuesSubmitMode,
    type BarcodeIssuesSuccess,
} from './barcodeIssuesMeta';

/** A read under way or failed while a result is on screen (rulebook §12). */
export type BarcodeIssuesRefresh = 'idle' | 'pending' | 'error';

/**
 * The list as the tab shows it (rulebook §12):
 * - `loading`: no page has arrived yet, so there is no count either;
 * - `error`: the first read failed; Retry reads it again;
 * - `empty`: nothing is listed at all (`totalCount` 0);
 * - `ready`: a page of rows. `isPlaceholder` marks the previous page of the
 *   same branch shown while the next one loads: its rows can't be picked or
 *   sent (review of #241, finding 4).
 */
export type BarcodeIssuesListState =
    | { status: 'loading' }
    | { status: 'error'; onRetry: () => void }
    | { status: 'empty'; refresh: BarcodeIssuesRefresh; onRetry: () => void }
    | {
          status: 'ready';
          data: BarcodeIssue[];
          totalCount: number;
          refresh: BarcodeIssuesRefresh;
          isPlaceholder: boolean;
          onRetry: () => void;
      };

/** The sticky bar: what is selected and what each button would send. */
export type BarcodeIssuesBarState = {
    selectedCount: number;
    /** The variants the selected rows belong to, which "Keep N" counts. */
    variantCount: number;
    /** Selected rows whose chosen fix generates a barcode. */
    toGenerateCount: number;
    /** Every selected row typing a code has an accepted one. */
    canApplyChosen: boolean;
    /** More rows than one call takes (`BARCODE_FIX_MAX_ITEMS`). */
    isTooMany: boolean;
    isApplying: boolean;
};

export type UseBarcodeIssuesResult = {
    list: BarcodeIssuesListState;
    page: number;
    pageSize: number;
    totalPages: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    selectedKeys: ReadonlySet<string>;
    /** The table's selection: a row's checkbox, or the header's for the page. */
    onSelectionChange: (keys: ReadonlySet<string>) => void;
    choiceOf: (issue: BarcodeIssue) => BarcodeIssueChoice;
    /** Picks a row's fix; the row is selected with it. */
    onFixChange: (key: string, fix: BarcodeFixAction) => void;
    onTypedChange: (key: string, typed: string) => void;
    onUseAnyway: (key: string) => void;
    /** The refusal code shown on each row it blames. */
    rowErrors: Readonly<Record<string, BarcodeIssuesRefusal>>;
    refusal: BarcodeIssuesRefusal | null;
    /** Reload the list, after `BARCODE_CHANGED` or an unknown outcome. */
    onReload: () => void;
    success: BarcodeIssuesSuccess | null;
    bar: BarcodeIssuesBarState;
    onSubmit: (mode: BarcodeIssuesSubmitMode) => void;
};

const EMPTY: readonly BarcodeIssue[] = [];

/**
 * Drives the Barcode issues tab (bos-app#239, AC-09, AC-10): one page of
 * `GET products/barcode-issues` for the tab's branch, the rows selected on
 * it, each row's chosen fix, and the one `fixBarcodeIssues` call the sticky
 * bar sends with every selected row and the same `branchId`, so the
 * generated barcodes' `units` count that branch's stock.
 *
 * Selection is per page: a new page, page size or branch clears it, and so
 * does a fix that went through. A refusal keeps everything as it was (the
 * API applied nothing) and marks the row it blames; changing that row clears
 * its mark. The mutation's success refetches the list and the tab's count.
 *
 * A fix belongs to the branch it was sent for (review of #241, finding 6):
 * changing the branch drops the last result and every choice, an answer that
 * lands after the branch changed is ignored, and a fix that went through
 * clears only the rows it sent. Rows of the previous page shown while the
 * next loads can't be picked or sent (finding 4); a fix that empties a later
 * page goes back to the last page there is (finding 5).
 */
export const useBarcodeIssues = (
    branchId: string | null,
    enabled = true,
): UseBarcodeIssuesResult => {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(PRODUCT_DEFAULT_PAGE_SIZE);
    const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
        () => new Set(),
    );
    const [choices, setChoices] = React.useState<
        Record<string, BarcodeIssueChoice>
    >({});
    const [rowErrors, setRowErrors] = React.useState<
        Record<string, BarcodeIssuesRefusal>
    >({});
    const [refusal, setRefusal] = React.useState<BarcodeIssuesRefusal | null>(
        null,
    );
    const [success, setSuccess] = React.useState<BarcodeIssuesSuccess | null>(
        null,
    );

    // The branch an answer must still be for when it lands.
    const currentBranch = React.useRef(branchId);
    React.useLayoutEffect(() => {
        currentBranch.current = branchId;
    }, [branchId]);

    useResetOnKeyChange(branchId, () => {
        setPage(1);
        setSelectedKeys(new Set());
        setChoices({});
        setRefusal(null);
        setRowErrors({});
        setSuccess(null);
    });

    const query = useBarcodeIssuesQuery({ branchId, page, pageSize }, enabled);
    const mutation = useFixBarcodeIssuesMutation();

    const data = query.data;
    const isPlaceholder = query.isPlaceholderData === true;
    const issues = data?.items ?? EMPTY;
    const totalPages = data?.totalPages ?? 0;
    const pageWindow = React.useMemo(
        () => getPaginationWindow(totalPages, page),
        [page, totalPages],
    );

    // A fix can take a later page's last rows: its fresh answer is empty
    // while earlier pages still list some. Go back to the last page there is.
    React.useEffect(() => {
        if (
            data !== undefined &&
            !isPlaceholder &&
            data.items.length === 0 &&
            data.totalCount > 0 &&
            page > 1
        ) {
            setPage(Math.max(data.totalPages, 1));
        }
    }, [data, isPlaceholder, page]);

    // Only rows on this page can be selected; a refetch that drops a row
    // drops it from the selection too.
    const selectedIssues = React.useMemo(
        () =>
            issues.filter((issue) =>
                selectedKeys.has(barcodeIssueKeyOf(issue)),
            ),
        [issues, selectedKeys],
    );

    const clearRowError = React.useCallback((key: string) => {
        setRowErrors((current) => {
            if (!(key in current)) {
                return current;
            }
            const next = { ...current };
            delete next[key];
            return next;
        });
    }, []);

    const onSelectionChange = React.useCallback(
        (keys: ReadonlySet<string>) => {
            if (!isPlaceholder) {
                setSelectedKeys(new Set(keys));
            }
        },
        [isPlaceholder],
    );

    const issueByKey = React.useMemo(
        () => new Map(issues.map((issue) => [barcodeIssueKeyOf(issue), issue])),
        [issues],
    );

    const updateChoice = React.useCallback(
        (key: string, patch: Partial<BarcodeIssueChoice>) => {
            const issue = issueByKey.get(key);
            if (issue === undefined || isPlaceholder) {
                return false;
            }
            setChoices((current) => ({
                ...current,
                [key]: { ...choiceOf(current, issue), ...patch },
            }));
            clearRowError(key);
            return true;
        },
        [clearRowError, isPlaceholder, issueByKey],
    );

    const onFixChange = React.useCallback(
        (key: string, fix: BarcodeFixAction) => {
            if (updateChoice(key, { fix })) {
                setSelectedKeys((current) => new Set(current).add(key));
            }
        },
        [updateChoice],
    );

    const onTypedChange = React.useCallback(
        (key: string, typed: string) => {
            updateChoice(key, { typed });
        },
        [updateChoice],
    );

    const onUseAnyway = React.useCallback(
        (key: string) => {
            const issue = issueByKey.get(key);
            if (issue === undefined) {
                return;
            }
            updateChoice(key, {
                usedAnyway: choiceOf(choices, issue).typed.trim(),
            });
        },
        [choices, issueByKey, updateChoice],
    );

    const clearSelection = React.useCallback(() => {
        setSelectedKeys(new Set());
        setRowErrors({});
        setRefusal(null);
    }, []);

    const onPageChange = React.useCallback(
        (next: number) => {
            setPage(Math.min(Math.max(next, 1), Math.max(totalPages, 1)));
            clearSelection();
        },
        [clearSelection, totalPages],
    );

    const onPageSizeChange = React.useCallback(
        (size: number) => {
            setPageSize(size);
            setPage(1);
            clearSelection();
        },
        [clearSelection],
    );

    const onSubmit = React.useCallback(
        (mode: BarcodeIssuesSubmitMode) => {
            const sent = selectedIssues;
            if (
                isPlaceholder ||
                sent.length === 0 ||
                sent.length > BARCODE_FIX_MAX_ITEMS ||
                mutation.isPending
            ) {
                return;
            }
            if (
                mode === 'chosen' &&
                !sent.every((issue) => isChoiceReady(choiceOf(choices, issue)))
            ) {
                return;
            }
            const sentFor = branchId;
            const sentKeys = new Set(sent.map(barcodeIssueKeyOf));
            const isStillOwner = () => currentBranch.current === sentFor;
            setRefusal(null);
            setRowErrors({});
            setSuccess(null);
            mutation.mutate(
                {
                    items: buildFixItems(sent, mode, choices),
                    branchId,
                },
                {
                    onSuccess: (result) => {
                        if (!isStillOwner()) {
                            return;
                        }
                        setSuccess({
                            fixed: result.fixed,
                            generatedBarcodes: result.generatedBarcodes,
                            fixedAt: new Date().toISOString(),
                        });
                        setSelectedKeys((current) =>
                            withoutKeys(current, sentKeys),
                        );
                        setChoices((current) =>
                            withoutEntries(current, sentKeys),
                        );
                    },
                    onError: (error) => {
                        if (!isStillOwner()) {
                            return;
                        }
                        const read = readRefusal(error, sent);
                        setRefusal(read);
                        if (read.kind === 'row') {
                            setRowErrors({ [read.rowKey]: read });
                        }
                    },
                },
            );
        },
        [branchId, choices, isPlaceholder, mutation, selectedIssues],
    );

    const { refetch } = query;
    const onReload = React.useCallback(() => {
        clearSelection();
        setChoices({});
        void refetch();
    }, [clearSelection, refetch]);

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    const list = React.useMemo<BarcodeIssuesListState>(() => {
        if (data === undefined) {
            return query.isError
                ? { status: 'error', onRetry }
                : { status: 'loading' };
        }
        const refresh: BarcodeIssuesRefresh = query.isError
            ? 'error'
            : query.isFetching
              ? 'pending'
              : 'idle';
        if (data.totalCount === 0) {
            return { status: 'empty', refresh, onRetry };
        }
        return {
            status: 'ready',
            data: data.items,
            totalCount: data.totalCount,
            refresh,
            isPlaceholder,
            onRetry,
        };
    }, [data, isPlaceholder, onRetry, query.isError, query.isFetching]);

    const bar = React.useMemo<BarcodeIssuesBarState>(
        () => ({
            selectedCount: selectedIssues.length,
            variantCount: variantCountOf(selectedIssues),
            toGenerateCount: selectedIssues.filter(
                (issue) => choiceOf(choices, issue).fix === 'generate',
            ).length,
            canApplyChosen: selectedIssues.every((issue) =>
                isChoiceReady(choiceOf(choices, issue)),
            ),
            isTooMany: selectedIssues.length > BARCODE_FIX_MAX_ITEMS,
            isApplying: mutation.isPending,
        }),
        [choices, mutation.isPending, selectedIssues],
    );

    const rowChoiceOf = React.useCallback(
        (issue: BarcodeIssue) => choiceOf(choices, issue),
        [choices],
    );

    return {
        list,
        page,
        pageSize,
        totalPages,
        pageWindow,
        onPageChange,
        onPageSizeChange,
        selectedKeys,
        onSelectionChange,
        choiceOf: rowChoiceOf,
        onFixChange,
        onTypedChange,
        onUseAnyway,
        rowErrors,
        refusal,
        onReload,
        success,
        bar,
        onSubmit,
    };
};

const withoutKeys = (
    current: ReadonlySet<string>,
    gone: ReadonlySet<string>,
): Set<string> => new Set([...current].filter((key) => !gone.has(key)));

const withoutEntries = <T>(
    current: Readonly<Record<string, T>>,
    gone: ReadonlySet<string>,
): Record<string, T> =>
    Object.fromEntries(
        Object.entries(current).filter(([key]) => !gone.has(key)),
    );

/** The tab's inputs, which the boundary resets on and the Content reads. */
export type BarcodeIssuesProps = {
    /** The Products list's branch, which the list and the fix both send. */
    branchId: string | null;
    /** `false` while the branch list still checks the address's branch. */
    isEnabled: boolean;
};
